import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifFormField,
  MotifSelect,
  MotifOption,
  MotifLabel,
  MotifInput,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import { actionIcDescription24px } from "@ey-xd/motif-react/assets/icons";
import "./uploadEditDocument.css";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { useCase } from "@/features/case/use-case";
import {
  useLazyGetDocumentTypesQuery,
  useUploadDocumentMutation,
  useUpdateDocumentMutation,
  fileExtensions,
  useLazyDownloadDocumentQuery,
} from "@/services/documents/documentsReducer";
import {
  CANCEL_BUTTON_VARIANT,
  GetCommonDownloadImgButton,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";

export default function UploadEditDocument({
  show,
  setShow,
  claimId,
  isEdit,
  document,
}: {
  show: boolean;
  setShow: (isVisible: boolean) => void;
  claimId: string;
  isEdit: boolean;
  document: any;
}) {
  const { t } = useTranslation();
  const caseData = useCase();
  const { caseId } = caseData;
  const formData = new FormData();
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showDiscardMessage, setShowDiscardMessage] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [comment, setComment] = useState("");
  const [fileName, setFileName] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [getDocumentTypesQuery] = useLazyGetDocumentTypesQuery();
  const [uploadDocument] = useUploadDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [errorMessage, setErrorMessage] = useState("");

  const [downloadDocument, { isLoading: downloadLoading }] =
    useLazyDownloadDocumentQuery();

  const handleDownload = (documentData: any) => {
    setErrorMessage("");
    let fileExtensionName = documentData?.fileName?.split(".");
    let documentName = "";
    if (fileExtensionName.length > 1 && documentData?.displayName) {
      documentName = `${documentData.displayName}.${
        fileExtensionName[fileExtensionName?.length - 1]
      }`;
    } else {
      documentName = documentData.fileName;
    }
    downloadDocument({
      caseId: caseId,
      documentId: documentData.id,
      fileName: documentName,
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setErrorMessage("Something went wrong!!");
            console.error("File download error:", error);
          }
        }
      })
      .catch((error: any) => {
        setErrorMessage("Something went wrong!!");
        console.error("File download error:", error);
      });
  };
  useEffect(() => {
    getDocumentTypesQuery().then((response) => {
      if (response.data) {
        setDocumentTypes(response.data);
      }
    });
    // eslint-disable-next-line
  }, []);
  let [editFormData, setEditFormData] = useState({
    fileName: "",
    documentType: "",
    comment: "",
    file: new File([""], ""),
  });
  useEffect(() => {
    if (isEdit) {
      const type: any = documentTypes.filter((x: any) => {
        return x.name === document.documentType;
      });
      setFileName(document.displayName);
      setDocumentType(type.length !== 0 ? type[0].id : "");
      setComment(document.comment);
      setFile(new File([""], document.fileName));
      setEditFormData({
        fileName: document.displayName,
        documentType: type.length !== 0 ? type[0].id : "",
        comment: document.comment,
        file: new File([""], document.fileName),
      });
    }
    // eslint-disable-next-line
  }, [documentTypes]);

  const handleFileChange = (files: any) => {
    const file = files[0];
    if (file) {
      let displayFileName = file.name.slice(0, file.name.lastIndexOf("."));
      if (displayFileName.length > 100) {
        displayFileName = displayFileName.slice(0, 100);
      }
      setFileName(displayFileName);
      setFile(file);
    }
  };

  const handleSubmit = () => {
    setIsUploading(true);
    formData.append("CaseId", caseId);
    formData.append("DisplayName", fileName);
    formData.append("Comment", comment);
    formData.append("DocumentTypeId", documentType);

    if (isEdit) {
      formData.append("DocumentId", document.id);
      if (file && file.size !== 0) {
        formData.append("file", file);
      }
      updateDocument(formData).then(() => {
        setIsUploading(false);
        setShow(false);
      });
    } else {
      formData.append("EntityId", claimId);
      formData.append("EntityType", "2");

      if (file) {
        formData.append("file", file);
      }
      uploadDocument(formData).then(() => {
        setIsUploading(false);
        setShow(false);
      });
    }
  };
  function charLimitIndicator(value: string) {
    return (
      <div className="display-flex justify-content-end">
        <div className="motif-body2-default-regular title">
          {value?.length || 0}/100
        </div>
      </div>
    );
  }
  let discardtrig = false;
  if (
    (file !== null ||
      fileName !== "" ||
      documentType !== "" ||
      comment !== "") &&
    isEdit === false
  ) {
    discardtrig = true;
  } else if (isEdit) {
    if (
      !(
        JSON.stringify(file) === JSON.stringify(editFormData.file) &&
        fileName === editFormData.fileName &&
        documentType === editFormData.documentType &&
        comment === editFormData.comment
      )
    ) {
      discardtrig = true;
    }
  }
  return (
    <>
      {errorMessage && (
        <div className="pad-b-10">
          <NotificationGrid
            show={true}
            type="error"
            message={errorMessage}
            scrollToTop={false}
          />
        </div>
      )}
      <MotifModal
        className="upload-edit-document-container"
        show={show}
        onClose={() => {
          if (discardtrig) {
            setShowDiscardMessage(true);
          } else {
            setShow(false);
          }
        }}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader>
          {isEdit
            ? t("pages.claims.documentPopup.editTitle")
            : t("pages.claims.documentPopup.title")}
        </MotifModalHeader>
        <MotifModalBody>
          {documentTypes.length === 0 ? (
            <MotifProgressLoader show variant="default" />
          ) : (
            <>
              {t("pages.claims.documentPopup.subtitle")}
              <div className="motif-row document-inputs">
                <div className="motif-col-md-2 motif-col-lg-4">
                  <MotifLabel aria-label="Input" for="input">
                    {t("pages.claims.documentPopup.fileName")}
                    <span className="required-input">*</span>
                  </MotifLabel>
                  <MotifFormField aria-label="Input" role="group">
                    <MotifInput
                      // @ts-ignore
                      onChange={(e) => setFileName(e.target.value)}
                      onFocus={function Ua() {}}
                      value={fileName}
                      maxLength={100}
                    />
                    {charLimitIndicator(fileName)}
                  </MotifFormField>
                </div>
                <div className="motif-col-md-2 motif-col-lg-4">
                  <MotifFormField>
                    <MotifLabel id="select-label">
                      {t("pages.claims.documentPopup.documentType")}
                    </MotifLabel>
                    <MotifSelect
                      onChange={(value) => setDocumentType(value)}
                      value={documentType}
                      visibleOptions={3}
                    >
                      {documentTypes.map((option: any) => {
                        return (
                          <MotifOption value={option.id} key={option.id}>
                            {option.name}
                          </MotifOption>
                        );
                      })}
                    </MotifSelect>
                  </MotifFormField>
                </div>
                <div className="motif-col-md-2 motif-col-lg-4">
                  <MotifLabel aria-label="Input" for="input">
                    {t("pages.claims.documentPopup.comment")}
                  </MotifLabel>
                  <MotifFormField aria-label="Input" role="group">
                    <MotifInput
                      // @ts-ignore
                      onChange={(e) => setComment(e.target.value)}
                      onFocus={function Ua() {}}
                      value={comment}
                      maxLength={100}
                    />
                    {charLimitIndicator(comment)}
                  </MotifFormField>
                </div>
              </div>

              <div className="document-uploader">
                <MotifFileUploader
                  label={t("pages.claims.documentPopup.uploadText")}
                  labelDescription={t(
                    "pages.claims.documentPopup.uploadDescription"
                  )}
                  onDrop={(files) => handleFileChange(files)}
                  maxFiles={1}
                  accept={fileExtensions.default}
                >
                  {file && (
                    <MotifFileUploaderItem
                      fileIcon={<MotifIcon src={actionIcDescription24px} />}
                      fileName={file.name}
                      uploaded={document?.id ? false : true}
                      customButton={
                        document?.id && (
                          <GetCommonDownloadImgButton
                            loading={downloadLoading}
                            onClick={() => {
                              handleDownload(document);
                            }}
                          />
                        )
                      }
                      uploadedText={
                        t("pages.claims.documentPopup.displayName") + fileName
                      }
                      error={
                        (file && file.size >= fileExtensions.maxFileSize) ||
                        false
                      }
                      errorText={t(
                        "pages.claims.stageStatusPopup.filesizeExceedsLimit",
                        {
                          filesize: "250",
                        }
                      )}
                    />
                  )}
                </MotifFileUploader>
              </div>
            </>
          )}
        </MotifModalBody>
        <MotifModalFooter>
          <div className="document-footer">
            <MotifButton
              onClick={() => {
                if (discardtrig) {
                  setShowDiscardMessage(true);
                } else {
                  setShow(false);
                }
              }}
              variant={CANCEL_BUTTON_VARIANT}
            >
              {t("pages.claims.documentPopup.cancel")}
            </MotifButton>

            <MotifButton
              onClick={handleSubmit}
              variant={SAVE_BUTTON_VARIANT}
              type={isUploading ? "loader" : "submit"}
              disabled={
                (file && file.size >= fileExtensions.maxFileSize) ||
                file === null ||
                fileName === "" ||
                isUploading
              }
            >
              {isEdit
                ? t("pages.claims.documentPopup.save")
                : t("pages.claims.documentPopup.upload")}
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>

      <ConfirmationModal
        isVisible={showDiscardMessage}
        setIsVisible={setShowDiscardMessage}
        cancelBtnApproveHandler={() => setShow(false)}
        cancelBtnRejectHandler={() => setShowDiscardMessage(false)}
      />
    </>
  );
}
